import { IDirectionCount } from "./DirectionCount";
import { IPositionCount } from "./PositionCount";
import { IPositionsMean } from '../sample/ReportBodyData';

export interface IStatus {
    id: string;
    name: string;
}

// new
export interface ICount {
    completedCounts: number;
    totalCounts: number;
}

export interface IStatistics {
    covariance: number;
    directionId: string;
    mean: number;
    median: number;
    position: number;
    propertyId: string;
    standardDeviation: number
}



// direction data
export interface ICountData {
    position: number;
    positionName: string;
    directions: IDirectionCountChildData[];
    selected: boolean;
}

export interface IDirectionCountChildData {
    id: string;
    name: string;
    completedCounts: number;
    totalCounts: number;
    selected: boolean;
    maxCount: number;
}

export interface IKeyedInReadings {
    sampleId: string;
    propertyId: string;
    measurementId: string;
    position: number;
    directionId: string;
    count: number;
    value: number | string;
    valueRange?: any;
    completed?: boolean;
}

export interface IPropertyMean {
    mean: number
}

export interface IKeyedInDetails {
    id: string;
    name: string;
    counts: ICount[];
    countData: ICountData[];
    completionPercentage: string;
    status: IStatus;
    positionCounts: IPositionCount[];
    unit: string;
    readings: IKeyedInReadings[];
    avg?: any;
    directionCounts?: IDirectionCount[];
    dashboardReading: IKeyedInReadings[];
    propertyMean: IPropertyMean;
    positionMeans: IPositionsMean[];
    statistics: IStatistics[];
    setPositionVal?: number;
    setPosition(index: number): void;
    updateDirectionCount(position: number, directionId: string, completedCounts: number): void;
    setSelectedDirection(position: number, id: string): void;
    setCount(counts: ICount[]): void;
    setAverage(propertyId: string, directionId: string, position: any, statistics: any): void;
    setValue(item: any, val: string): void;
    setReadings(): void;
    setPositionValue(position: number): void,
    decimals:number
}

interface Dictionary<T> {
    [Key: string]: T;
}

export class KeyedInDetails implements IKeyedInDetails {
    id: string;
    name: string;
    counts: ICount[];
    directionCounts: IDirectionCount[];
    countData: ICountData[];
    completionPercentage: string;
    avg: any;
    status: IStatus;
    positionCounts: IPositionCount[];
    unit: string;
    readings: IKeyedInReadings[];
    dashboardReading: IKeyedInReadings[];
    propertyMean: IPropertyMean;
    positionMeans: IPositionsMean[];
    statistics: IStatistics[];
    setPositionVal?: number;
    decimals:number;

    constructor(id: string,
        name: string,
        counts: ICount[],
        directionCounts: IDirectionCount[],
        status: IStatus,
        positionCounts: IPositionCount[],
        unit: string,
        readings: IKeyedInReadings[],
        propertyMean: IPropertyMean,
        positionMeans: IPositionsMean[],
        statistics: IStatistics[],
        decimals:number,
    ) {
        this.id = id;
        this.name = name;
        this.counts = counts;
        this.directionCounts = directionCounts;
        this.positionCounts = positionCounts;
        this.decimals = decimals;
        if (this.positionCounts.length > 0) {
            this.positionCounts[0].selected = true
        }
        this.unit = unit;
        this.completionPercentage = "0";
        this.countData = [{
            position: -1,
            positionName: "",
            directions: [
                {
                    id: "All",
                    name: "All",
                    completedCounts: 0,
                    totalCounts: 0,
                    selected: true,
                    maxCount: 0
                }
            ],
            selected: true
        }];

        if (readings !== null || readings !== undefined) {
            this.dashboardReading = readings;
        } else {
            this.dashboardReading = [];
        }

        if (statistics === undefined || statistics === null) {
            this.statistics = [];
        } else {
            this.statistics = statistics;
        }

        if (this.id !== id) {
            this.setPositionVal = 0;
        }


        this.readings = [];
        this.propertyMean = propertyMean;
        this.positionMeans = positionMeans;
        this.status = status;

        this.setAllDirections();
        this.setReadings();
        this.setCount(counts);
        this.setPositionValue(0);
    }

    public setPositionValue(position: number) {
        this.setPositionVal = position;
    }

    public setCount(counts: ICount[]) {
        this.counts = [...counts];
        if (counts.length > 0) {
            this.completionPercentage = ((counts[0].completedCounts / counts[0].totalCounts) * 100) + "%";
        }
    }

    private setAllDirections() {
        if (this.directionCounts.length > 0) {
            this.countData = [];
        }
        const maxCountMapping: Dictionary<number> = {};

        this.directionCounts.forEach((entity) => {
            if (this.countData.some(x => x.position === entity.position)) {

                const count = this.countData.find(x => x.position === entity.position);

                if (maxCountMapping[entity.direction.id] === undefined) {
                    maxCountMapping[entity.direction.id] = entity.totalCounts
                } else {
                    maxCountMapping[entity.direction.id] = Math.max(maxCountMapping[entity.direction.id], entity.totalCounts);
                }

                count.directions.push({
                    id: entity.direction.id,
                    name: entity.direction.name,
                    completedCounts: entity.completedCounts,
                    totalCounts: entity.totalCounts,
                    selected: false,
                    maxCount: maxCountMapping[entity.direction.id]
                });

            } else {

                if (maxCountMapping[entity.direction.id] === undefined) {
                    maxCountMapping[entity.direction.id] = entity.totalCounts
                } else {
                    maxCountMapping[entity.direction.id] = Math.max(maxCountMapping[entity.direction.id], entity.totalCounts);
                }

                const direction = {
                    id: entity.direction.id,
                    name: entity.direction.name,
                    completedCounts: entity.completedCounts,
                    totalCounts: entity.totalCounts,
                    selected: false,
                    maxCount: maxCountMapping[entity.direction.id]
                }

                const count = {
                    position: entity.position,
                    positionName: entity.name,
                    directions: [direction],
                    selected: false
                }

                this.countData.push(count);
            }
        });
        this.countData[0].directions[0].selected = true;
        this.countData[0].selected = true;

    }

    public updateDirectionCount(position: number, directionId: string, completedCounts: number) {
        const directionCount = this.directionCounts.find(x => x.direction.id === directionId && x.position === position);
        const directionCountsIndex = this.directionCounts.findIndex(x => x.direction.id === directionId && x.position === position);

        directionCount.completedCounts = completedCounts;
        this.directionCounts[directionCountsIndex] = directionCount;
    }

    public setPosition(index: number) {
        this.setPositionVal = index;
        const prevPositionCounts = this.positionCounts.find(x => x.selected);
        if (prevPositionCounts !== undefined) {
            prevPositionCounts.selected = false;
        }
        const toBePositionCounts = this.positionCounts.find(x => x.position === index);
        if (toBePositionCounts !== undefined) {
            toBePositionCounts.selected = true;
        }

        const prevCountData = this.countData.find(x => x.selected);
        if (prevCountData !== undefined) {
            prevCountData.selected = false;
        }
        const toBeCountData = this.countData.find(x => x.position === index)
        if (toBeCountData !== undefined) {
            toBeCountData.selected = true;
            toBeCountData.directions[0].selected = true;
        }
    }

    public setSelectedDirection(position: number, id: string) {
        const countData = this.countData.find(x => x.position === position);
        const prevSelectedDirection = countData.directions.find(y => y.selected);
        if (prevSelectedDirection !== undefined) {
            prevSelectedDirection.selected = false;
        }
        const toBeSelectedDirection = countData.directions.find(x => x.id === id);
        if (toBeSelectedDirection !== undefined) {
            toBeSelectedDirection.selected = true;
        }

    }

    public setAverage(propertyId: string, directionId: string, position: any, statistic: any) {
        if (this.id === propertyId) {
            this.avg = statistic.mean;
        }
        const dt = this.statistics.find(x => x.propertyId === propertyId && x.position === position && x.directionId === directionId);
        if (dt === undefined) {
            this.statistics.push({
                covariance: statistic?.covariance || 0,
                directionId,
                mean: statistic?.mean,
                median: statistic?.median,
                position,
                propertyId,
                standardDeviation: statistic?.standardDeviation
            });
        } else {
            dt.mean = statistic.mean
        }
    }

    public setReadings() {
        if (this.readings !== undefined || this.readings !== null) {
            const countData = this.countData.find(x => x.selected);
            const selectedDirection = countData.directions.find(x => x.selected);
            if (selectedDirection !== undefined) {
                if (this.dashboardReading.length > 0) {
                    this.dashboardReading.map((x: IKeyedInReadings, index: number) => {
                        this.readings.push({
                            sampleId: "",
                            propertyId: this.id,
                            measurementId: "",
                            position: x.position,
                            directionId: x.directionId,
                            count: x.count,
                            value: x.value,
                            completed: false
                        })
                    })
                } else {
                    for (let index = 0; index < selectedDirection?.maxCount; index++) {
                        const data = this.readings.some(x => x.directionId === selectedDirection.id && x.count === index + 1)
                        if (data !== undefined || data !== null) {
                            this.readings.push({
                                sampleId: "",
                                propertyId: this.id,
                                measurementId: "",
                                position: -1,
                                directionId: selectedDirection.id,
                                count: index + 1,
                                value: 0,
                                completed: false
                            })
                        }
                    }
                }

            }
        }
    }

    public setValue(item: any, val: string) {

        const reading = this.readings.find(x => x.propertyId === item.propertyId && x.count === item.count
            && x.directionId === item.directionId);

        const readingIndex = this.readings.findIndex(x => x.propertyId === item.propertyId && x.count === item.count
            && x.directionId === item.directionId);

        this.readings[readingIndex].value = Number(val);
    }


}

const defaultKeyedInDetails = new KeyedInDetails("", "", [], [], { id: "", name: "" }, [], "", [], { mean: 0 }, [], [],0);
export { defaultKeyedInDetails };
